import { dlog } from '../utils/debug';
import React, { useState, useEffect, useRef } from 'react';
import './DynamicFlapper.css';
import FaceHalf from './FaceHalf';
import {
  calculatePerformanceEmotion,
  getMostUsedColor,
  inferMyTeam,
  EMOTIONS
} from '../utils/emotionEngine';

const DEFAULT_BG_COLOR = '#C4B5A0';

const DynamicFlapper = ({ 
  matches = [], 
  size = 'medium', 
  showTooltip = true, 
  mode = 'logo',
  isLoading = false, // Whether matches are currently loading
  loaderBaseColor = '#FFF2DD', // Base color for loader (defaults to app background)
  loaderDarkness1 = 0.18, // First darkness level (18% darker)
  loaderDarkness2 = 0.22  // Second darkness level (22% darker)
}) => {
  // Helper to calculate initial loader color
  const getInitialLoaderColor = () => {
    const hex = loaderBaseColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const darkenFactor = loaderDarkness1;
    const newR = Math.round(r * (1 - darkenFactor));
    const newG = Math.round(g * (1 - darkenFactor));
    const newB = Math.round(b * (1 - darkenFactor));
    const toHex = (c) => {
      const hex = Math.max(0, Math.min(255, c)).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
  };

  // Helper to get initial resting color (darkened bg color for logo mode)
  const getInitialRestingColor = () => {
    if (mode === 'loader') return getInitialLoaderColor();
    // For logo mode, use darkened version of app background
    return getInitialLoaderColor(); // Use same darkening logic
  };

  // Default colors for no-matches state
  const DEFAULT_COLOR = '#FFB84D'; // Gold-orange (denotes winning/positive state)
  const DEFAULT_EMOTION = 'joyful';
  
  // State for visible (back) layer - start with default colors for logo mode
  const [leftColor, setLeftColor] = useState(mode === 'logo' ? DEFAULT_COLOR : getInitialRestingColor());
  const [rightColor, setRightColor] = useState(mode === 'logo' ? DEFAULT_COLOR : getInitialRestingColor());
  // Don't set emotions initially - wait until we know what to show
  const [leftEmotion, setLeftEmotion] = useState(mode === 'logo' ? null : 'default');
  const [rightEmotion, setRightEmotion] = useState(mode === 'logo' ? null : 'default');
  
  // State for animating (front) layer
  const [leftColorFront, setLeftColorFront] = useState(mode === 'logo' ? DEFAULT_COLOR : getInitialRestingColor());
  const [rightColorFront, setRightColorFront] = useState(mode === 'logo' ? DEFAULT_COLOR : getInitialRestingColor());
  const [leftEmotionFront, setLeftEmotionFront] = useState(mode === 'logo' ? null : 'default');
  const [rightEmotionFront, setRightEmotionFront] = useState(mode === 'logo' ? null : 'default');
  
  // Track if we're ready to show content (not loading anymore) - always start false in logo mode
  const [isReady, setIsReady] = useState(false);
  
  const [isAnimatingLeft, setIsAnimatingLeft] = useState(false);
  const [isAnimatingRight, setIsAnimatingRight] = useState(false);
  const prevMatchesRef = useRef(null);
  const animateSide = useRef(true); // Track which side to animate next (true = left, false = right)
  const leftDarknessLevel = useRef(1); // Track left side darkness level
  const rightDarknessLevel = useRef(1); // Track right side darkness level
  const loaderInitialized = useRef(false); // Track if loader has been started
  const loadingAnimationActive = useRef(false); // Track if loading animation is active
  const currentCycleLevel = useRef(1); // Track current cycle level (1, 2, or 3)
  const currentFlapInCycle = useRef(0); // Track which flap in current cycle (0 = left, 1 = right)
  const noMatchTimeout = useRef(null); // Track timeout for no-match animation
  const hasInitialized = useRef(false); // Track if component has initialized with data
  const animationTimers = useRef([]); // Track all animation timers for cleanup

  // Loader mode: no border, no face, simpler
  const isLoader = mode === 'loader';
  
  // Function to generate a color at a specific darkness level
  // Using a simpler approach that directly darkens the RGB values
  const generateColor = (darknessLevel) => {
    // Parse the hex color
    const hex = loaderBaseColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Apply darken filter based on level
    const darkenFactor = (darknessLevel === 1) ? loaderDarkness1 : loaderDarkness2;
    
    // Darken by reducing each RGB component
    const newR = Math.round(r * (1 - darkenFactor));
    const newG = Math.round(g * (1 - darkenFactor));
    const newB = Math.round(b * (1 - darkenFactor));
    
    // Convert back to hex
    const toHex = (c) => {
      const hex = Math.max(0, Math.min(255, c)).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    
    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
  };
  const [currentCardIndex, setCurrentCardIndex] = useState(0);

  // Size system - always maintains 1:1 aspect ratio
  const sizeConfig = {
    tiny: { dimension: 32, viewBox: 32, featureScale: 0.8, strokeWidth: 4 },
    small: { dimension: 48, viewBox: 48, featureScale: 1.0, strokeWidth: 5 },
    medium: { dimension: 64, viewBox: 64, featureScale: 1.2, strokeWidth: 6 },
    large: { dimension: 80, viewBox: 80, featureScale: 1.4, strokeWidth: 7 }
  };
  const config = sizeConfig[size] || sizeConfig.medium;

  // Helper function to clear all animation timers
  const clearAllAnimationTimers = () => {
    animationTimers.current.forEach(timerId => clearTimeout(timerId));
    animationTimers.current = [];
    dlog('🧹 Cleared all animation timers');
  };

  // Effect that runs when matches change (including edits)
  useEffect(() => {
    // Loader mode: infinite flipping animation
    if (isLoader) {
      // Clear any pending animations from previous state (only when entering loader mode)
      if (!loaderInitialized.current) {
        clearAllAnimationTimers();
      }
      // Only initialize once
      if (loaderInitialized.current) {
        return;
      }
      
      dlog('🔄 Loader mode: starting infinite progressive stack animation');
      loaderInitialized.current = true;
      
      // Reset refs
      leftDarknessLevel.current = 1;
      rightDarknessLevel.current = 1;
      animateSide.current = true;
      
      // Set initial colors - both start with level 1 (slightly darker than background)
      setLeftColor(generateColor(1));
      setRightColor(generateColor(1));
      setLeftEmotion('default');
      setRightEmotion('default');
      
      // Start infinite animation loop
      const startInfiniteAnimation = () => {
        
        const isLeftSide = animateSide.current;
        
        if (isLeftSide) {
          // Animate left flap
          setTimeout(() => {
            // Toggle left side darkness level: 1 → 3 → 1 → 3...
            leftDarknessLevel.current = leftDarknessLevel.current === 1 ? 3 : 1;
            
            // Set front layer color for animation
            setLeftColorFront(generateColor(leftDarknessLevel.current));
            setIsAnimatingLeft(true);
            
            setTimeout(() => {
              // Update back layer first
              setLeftColor(generateColor(leftDarknessLevel.current));
              
              // Wait for back layer to render before hiding front
              setTimeout(() => {
                setIsAnimatingLeft(false);
              }, 50);
              
              // Switch to right side for next animation
              animateSide.current = false;
              
              // Continue the loop
              setTimeout(() => {
                startInfiniteAnimation();
              }, 300); // Small delay before next animation
            }, 800);
          }, 0);
        } else {
          // Animate right flap
          setTimeout(() => {
            // Toggle right side darkness level: 1 → 3 → 1 → 3...
            rightDarknessLevel.current = rightDarknessLevel.current === 1 ? 3 : 1;
            
            // Set front layer color for animation
            setRightColorFront(generateColor(rightDarknessLevel.current));
            setIsAnimatingRight(true);
            
            setTimeout(() => {
              // Update back layer first
              setRightColor(generateColor(rightDarknessLevel.current));
              
              // Wait for back layer to render before hiding front
              setTimeout(() => {
                setIsAnimatingRight(false);
              }, 50);
              
              // Switch to left side for next animation
              animateSide.current = true;
              
              // Continue the loop
              setTimeout(() => {
                startInfiniteAnimation();
              }, 300); // Small delay before next animation
            }, 800);
          }, 0);
        }
      };
      
      startInfiniteAnimation();
      return;
    }
    
    // Reset loader flag if not in loader mode
    loaderInitialized.current = false;
    
    // If still loading in logo mode, start infinite flapping animation (no faces)
    if (isLoading && mode === 'logo') {
      dlog('⏭️  Loading - starting flapping animation without faces');
      setIsReady(false);
      
      // Only initialize once
      if (loadingAnimationActive.current) {
        return;
      }
      
      loadingAnimationActive.current = true;
      loaderInitialized.current = true;
      
      // Reset refs
      leftDarknessLevel.current = 1;
      rightDarknessLevel.current = 1;
      currentCycleLevel.current = 1;
      currentFlapInCycle.current = 0;
      
      // Set initial colors - both start with level 1 (slightly darker than background)
      setLeftColor(generateColor(1));
      setRightColor(generateColor(1));
      
      // Start infinite animation loop after a small delay to ensure smooth start
      const startLoadingAnimation = () => {
        // Check if still loading via ref (updated in effect)
        if (!loadingAnimationActive.current) {
          return;
        }
        
        // Determine target level for this cycle
        // Cycle: 1 → 2 → 3 → 1
        let targetLevel;
        if (currentCycleLevel.current === 1) {
          targetLevel = 2;
        } else if (currentCycleLevel.current === 2) {
          targetLevel = 3;
        } else {
          targetLevel = 1;
        }
        
        const isLeftSide = currentFlapInCycle.current === 0;
        
        if (isLeftSide) {
          // Animate left flap to target level
          const timer1 = setTimeout(() => {
            // Set front layer color for animation
            setLeftColorFront(generateColor(targetLevel));
            setIsAnimatingLeft(true);
            
            const timer2 = setTimeout(() => {
              // Update back layer
              setLeftColor(generateColor(targetLevel));
              leftDarknessLevel.current = targetLevel;
              
              // Wait for back layer to render before hiding front
              const timer3 = setTimeout(() => {
                setIsAnimatingLeft(false);
              }, 50);
              animationTimers.current.push(timer3);
              
              // Move to right flap in this cycle
              currentFlapInCycle.current = 1;
              
              // Continue with right flap
              const timer4 = setTimeout(() => {
                startLoadingAnimation();
              }, 300);
              animationTimers.current.push(timer4);
            }, 800);
            animationTimers.current.push(timer2);
          }, 0);
          animationTimers.current.push(timer1);
        } else {
          // Animate right flap to target level
          const timer1 = setTimeout(() => {
            // Set front layer color for animation
            setRightColorFront(generateColor(targetLevel));
            setIsAnimatingRight(true);
            
            const timer2 = setTimeout(() => {
              // Update back layer
              setRightColor(generateColor(targetLevel));
              rightDarknessLevel.current = targetLevel;
              
              // Wait for back layer to render before hiding front
              const timer3 = setTimeout(() => {
                setIsAnimatingRight(false);
              }, 50);
              animationTimers.current.push(timer3);
              
              // Both flaps are now at target level - cycle complete
              // Move to next cycle level
              currentCycleLevel.current = targetLevel;
              currentFlapInCycle.current = 0; // Reset to left for next cycle
              
              // Continue to next cycle
              const timer4 = setTimeout(() => {
                startLoadingAnimation();
              }, 300);
              animationTimers.current.push(timer4);
            }, 800);
            animationTimers.current.push(timer2);
          }, 0);
          animationTimers.current.push(timer1);
        }
      };
      
      // Start animation after a brief delay to ensure smooth initialization
      const initTimer = setTimeout(() => {
        startLoadingAnimation();
      }, 100);
      animationTimers.current.push(initTimer);
      return;
    }
    
    // If loading finished, stop the loading animation
    if (!isLoading && loadingAnimationActive.current) {
      dlog('✅ Loading complete - stopping flapping animation');
      loadingAnimationActive.current = false;
      clearAllAnimationTimers();
      setIsAnimatingLeft(false);
      setIsAnimatingRight(false);
    }
    
    // If loading but not in logo mode, just wait
    if (isLoading) {
      dlog('⏭️  Still loading - waiting...', { matches: matches?.length, isLoading });
      setIsReady(false);
      return;
    }
    
    // If no matches, animate in with default colors and happy face
    if (!matches || matches.length === 0) {
      // Create a unique key for "no matches" state
      const noMatchesKey = 'no-matches-state';
      
      // Check if we've already animated for this state
      if (prevMatchesRef.current === noMatchesKey) {
        dlog('⏭️  Already in no-matches state');
        return;
      }
      
      // Clear any pending animations before starting new ones (only if switching to no-matches)
      if (prevMatchesRef.current !== noMatchesKey) {
        clearAllAnimationTimers();
      }
      
      // Show default state
      dlog('🎯 No matches and not loading - showing default happy state');
      prevMatchesRef.current = noMatchesKey;
      
      // DON'T update back layer yet - keep it as-is (loading/default state)
      // Set front layer with default colors and happy emotion for animation
      dlog('🔴🔵 SETTING DEFAULT COLORS:', DEFAULT_COLOR);
      setLeftColorFront(DEFAULT_COLOR);
      setLeftEmotionFront(DEFAULT_EMOTION);
      setRightColorFront(DEFAULT_COLOR); // Use same color as left
      setRightEmotionFront(DEFAULT_EMOTION);
      
      // Mark as ready so faces can render (but only on front layer for now)
      setIsReady(true);
      dlog('✅ Set isReady to true for default colors');
      
      // Trigger animation - front layer flips to reveal new color/emotion
      const timer1 = setTimeout(() => {
        setIsAnimatingLeft(true);
        
        const timer2 = setTimeout(() => {
          // Update back layer to match the new color/emotion
          setLeftColor(DEFAULT_COLOR);
          setLeftEmotion(DEFAULT_EMOTION);
          
          // Wait for back layer to fully render before hiding front layer
          const timer3 = setTimeout(() => {
            setIsAnimatingLeft(false);
          }, 50);
          animationTimers.current.push(timer3);
        }, 800);
        animationTimers.current.push(timer2);
        
        const timer4 = setTimeout(() => {
          setIsAnimatingRight(true);
          
          const timer5 = setTimeout(() => {
            // Update back layer to match the new color/emotion
            setRightColor(DEFAULT_COLOR);
            setRightEmotion(DEFAULT_EMOTION);
            
            // Wait for back layer to fully render before hiding front layer
            const timer6 = setTimeout(() => {
              setIsAnimatingRight(false);
            }, 50);
            animationTimers.current.push(timer6);
          }, 800);
          animationTimers.current.push(timer5);
        }, 150);
        animationTimers.current.push(timer4);
      }, 50);
      animationTimers.current.push(timer1);
      
      return;
    }

    // Clear the no-match timeout since we have matches
    if (noMatchTimeout.current) {
      clearTimeout(noMatchTimeout.current);
      noMatchTimeout.current = null;
    }

    dlog('📊 Processing matches:', matches.length, 'matches found');
    dlog('📊 Match details:', matches.map(m => ({ id: m.id, teamA: m.teamA, teamB: m.teamB, score: `${m.teamAScore}-${m.teamBScore}` })));

    // Diff key for deciding whether to re-run the emotion engine and
    // re-animate the flaps. Only includes data that actually affects
    // emotion output (team names+colors for identity, setsWon for
    // performance, status for playing→final). Intentionally excludes
    // current-set scores and the mutable `sets` array — those tick on
    // every tap and re-firing the animation on every Firebase update
    // caused multi-device flashing on the viewer (#27).
    const currentMatchData = JSON.stringify(matches.map(match => ({
      id: match.id,
      teamA: match.teamA,
      teamB: match.teamB,
      teamAColor: match.teamAColor,
      teamBColor: match.teamBColor,
      setsWon: match.setsWon,
      status: match.status,
    })));

    // Check if match data has actually changed
    if (prevMatchesRef.current === currentMatchData) {
      dlog('⏭️  Skipping emotion calculation - no changes detected');
      dlog('   Previous:', prevMatchesRef.current?.substring(0, 100));
      dlog('   Current:', currentMatchData?.substring(0, 100));
      return;
    }
    
    dlog('✨ Match data changed! Animating...');
    dlog('   Previous:', prevMatchesRef.current?.substring(0, 100));
    dlog('   Current:', currentMatchData?.substring(0, 100));
    
    // Clear any pending animations before starting new ones (only if match data actually changed)
    clearAllAnimationTimers();
    
    // Mark as initialized when processing real match data
    hasInitialized.current = true;

    prevMatchesRef.current = currentMatchData;
    dlog('🎯 Calculating new emotions from matches:', matches.length);

    // Calculate emotions and colors
    const myTeam = inferMyTeam(matches);
    const performanceEmotion = calculatePerformanceEmotion(matches);
    const mostUsedColor = getMostUsedColor(matches, myTeam);
    
    // Both flaps: Use favorite team color and same emotion face
    const flapColor = mostUsedColor || DEFAULT_COLOR;
    const flapEmotion = performanceEmotion || DEFAULT_EMOTION;
    
    dlog('🎨 Color Debug:', { 
      myTeam,
      mostUsedColor, 
      performanceEmotion, 
      flapColor, 
      flapEmotion 
    });
    
    // DON'T update back layer yet - keep it as-is (loading/default state)
    // Set front layer with new team color/emotion for animation
    setLeftColorFront(flapColor);
    setLeftEmotionFront(flapEmotion);
    setRightColorFront(flapColor);
    setRightEmotionFront(flapEmotion);
    
    // Mark as ready so faces can render (but only on front layer for now)
    setIsReady(true);
    dlog('✅ Set isReady to true for match-based colors');

    // Trigger staggered animations after a small delay to ensure state is set
    const matchTimer1 = setTimeout(() => {
      // Start left flap animation - front layer flips to reveal new color/emotion
      setIsAnimatingLeft(true);
      
      // After animation completes, update back layer to match, then hide front layer
      const matchTimer2 = setTimeout(() => {
        // Update back layer to match the new color/emotion
        setLeftColor(flapColor);
        setLeftEmotion(flapEmotion);
        
        // Wait for back layer to fully render before hiding front layer
        const matchTimer3 = setTimeout(() => {
          setIsAnimatingLeft(false);
        }, 50); // Small delay to ensure back layer is painted
        animationTimers.current.push(matchTimer3);
      }, 800);
      animationTimers.current.push(matchTimer2);
      
      // Start right flap animation after delay
      const matchTimer4 = setTimeout(() => {
        setIsAnimatingRight(true);
        
        // After animation completes, update back layer to match, then hide front layer
        const matchTimer5 = setTimeout(() => {
          // Update back layer to match the new color/emotion
          setRightColor(flapColor);
          setRightEmotion(flapEmotion);
          
          // Wait for back layer to fully render before hiding front layer
          const matchTimer6 = setTimeout(() => {
            setIsAnimatingRight(false);
          }, 50); // Small delay to ensure back layer is painted
          animationTimers.current.push(matchTimer6);
        }, 800);
        animationTimers.current.push(matchTimer5);
      }, 150);
      animationTimers.current.push(matchTimer4);
    }, 50);
    animationTimers.current.push(matchTimer1);
    
    // Cleanup function to clear timeout on unmount or when effect re-runs
    return () => {
      // Only clear timers on unmount, not on every re-render
      if (noMatchTimeout.current) {
        clearTimeout(noMatchTimeout.current);
        noMatchTimeout.current = null;
      }
    };
  }, [matches, isLoading]);

  const sizes = {
    tiny: 0.5,
    small: 0.75,
    medium: 1,
    large: 1.25
  };

  const scale = sizes[size] || sizes.medium;
  const dimension = config.dimension * scale;
  
  // Scale up face size for smaller logos to improve readability
  const faceScaleMultiplier = size === 'tiny' ? 1.4 : size === 'small' ? 1.3 : 1;
  const faceSize = (dimension / 2) * faceScaleMultiplier;

  // Debug log for render
  dlog('🎭 Render state:', { 
    isReady, 
    isLoader, 
    isLoading, 
    leftColor, 
    rightColor, 
    leftEmotion, 
    rightEmotion,
    matchesCount: matches?.length 
  });

  return (
    <div 
      className={`dynamic-flapper ${mode === 'logo' ? 'logo-mode' : 'loader-mode'} size-${size}`}
      style={{ 
        width: dimension, 
        height: dimension,
        display: 'inline-block',
      }}
    >
      <div className="flapper-container">
        <div className="flapper-tile">
          {/* Left back flap - always visible */}
          <div className="flap back" style={{ backgroundColor: leftColor }}>
            {isLoader || isLoading || !isReady || !leftEmotion ? (
              <div className="loader-indicator">
              </div>
            ) : (
              <FaceHalf 
                emotion={leftEmotion} 
                side="left" 
                size={faceSize}
                className="face-half-back"
              />
            )}
          </div>
          
          {/* Left front flap - only visible during animation */}
          <div className={`flap front ${isAnimatingLeft ? 'flipping' : ''}`} style={{ backgroundColor: leftColorFront }}>
            {isLoader || isLoading || !leftEmotionFront ? (
              <div className="loader-indicator">
              </div>
            ) : (
              <FaceHalf 
                emotion={leftEmotionFront} 
                side="left" 
                size={faceSize}
                className="face-half-front"
              />
            )}
          </div>
        </div>
        
        <div className="flapper-tile">
          {/* Right back flap - always visible */}
          <div className="flap back" style={{ backgroundColor: rightColor }}>
            {isLoader || isLoading || !isReady || !rightEmotion ? (
              <div className="loader-indicator">
              </div>
            ) : (
              <FaceHalf 
                emotion={rightEmotion} 
                side="right" 
                size={faceSize}
                className="face-half-back"
              />
            )}
          </div>
          
          {/* Right front flap - only visible during animation */}
          <div className={`flap front ${isAnimatingRight ? 'flipping' : ''}`} style={{ backgroundColor: rightColorFront }}>
            {isLoader || isLoading || !rightEmotionFront ? (
              <div className="loader-indicator">
              </div>
            ) : (
              <FaceHalf 
                emotion={rightEmotionFront} 
                side="right" 
                size={faceSize}
                className="face-half-front"
              />
            )}
          </div>
        </div>
      </div>

      {/* Mouth is now included in the SVG faces, so no separate overlay needed */}
    </div>
  );
};

export default DynamicFlapper;
